/**
 * `/admin` view state that belongs in the URL.
 *
 * Which tab is open (and which division the bracket editor is showing) used
 * to be React state, so a reload dropped the organiser back on the default tab
 * and "here, look at the Open bracket" was not a link you could send.
 *
 * The TAB is the path segment (`/admin/schedule`); everything else here is
 * typed SEARCH. The tab was a search param too until 2026-09-03, because the
 * scorekeeper lived at `/admin/$field` and `isFieldId("bracket")` is true;
 * it moved to `/admin/field/$field` and the collision went with it.
 *
 * Lives in its own module because `validateSearch` and the tab check run on
 * the route, which is in the main chunk; pulling them from `AdminHomePage`
 * would drag the whole lazy admin bundle in with it.
 */

import type { TournamentStatus } from "../../shared/tournament";

export const ADMIN_TABS = [
  // Path vocabulary; display order is defined by ADMIN_NAV_GROUPS below.
  "tournament",
  "games",
  "teams",
  "divisions",
  "fields",
  "schedule",
  "bracket",
  "review",
  "spirit",
  "stats",
  "links",
  // The tournament's own audit trail (V2 Phase 6, `audit:read:own`).
  "activity",
] as const;

export type AdminTab = (typeof ADMIN_TABS)[number];

/** Stable group order on both the desktop rail and the phone section menu. */
export const ADMIN_NAV_GROUPS = [
  { id: "event", tabs: ["tournament", "games", "fields"] },
  { id: "setup", tabs: ["divisions", "teams"] },
  { id: "play", tabs: ["schedule", "bracket"] },
  { id: "after", tabs: ["review", "spirit", "stats"] },
  { id: "access", tabs: ["links", "activity"] },
] as const satisfies readonly { id: string; tabs: readonly AdminTab[] }[];

export const DEFAULT_ADMIN_TAB: AdminTab = "games";

/**
 * Where a bare `admin` lands: a DRAFT opens on its setup checklist, because
 * an invited organiser arrives there with nothing configured yet; a published
 * or archived event opens on the live fields like before. `undefined` (the
 * registry could not be asked) keeps the old default.
 */
export function defaultAdminTab(status: TournamentStatus | undefined): AdminTab {
  return status === "draft" ? "tournament" : DEFAULT_ADMIN_TAB;
}

/** Sub-view inside the Spirit tab. In the URL for the same reason the tab is:
 *  an organiser refreshing "who still owes" all afternoon should land back on
 *  it, and "look at the standings" should be a link. */
export const SPIRIT_VIEWS = ["missing", "scores", "standings", "settings"] as const;
export type SpiritViewParam = (typeof SPIRIT_VIEWS)[number];

export const DEFAULT_SPIRIT_VIEW: SpiritViewParam = "missing";

/** Sub-view inside the Stats tab, in the URL for the same reason. Shares the
 *  `view` param with Spirit: they are never open at once, each tab validates
 *  the value against its OWN list, and an unrecognised one falls back to that
 *  tab's default rather than rendering nothing. */
export const STATS_VIEWS = [
  "leaders",
  "teams",
  "coverage",
  "people",
  "awards",
  "settings",
] as const;
export type StatsViewParam = (typeof STATS_VIEWS)[number];

export const DEFAULT_STATS_VIEW: StatsViewParam = "leaders";

/**
 * Which SHAPE the Schedule tab draws its games in: the time-by-field grid, or
 * one column of time buckets. Shares `view` with Spirit and Stats on the same
 * terms — never open at once, validated against its own list, an unrecognised
 * value falling back to this tab's default.
 *
 * In the URL because the choice is a reading preference an organiser makes
 * once and expects to survive a reload, and because "look at Sunday" is a link
 * that should arrive looking the way the sender left it.
 */
export const SCHEDULE_VIEWS = ["grid", "list", "slots"] as const;
export type ScheduleViewParam = (typeof SCHEDULE_VIEWS)[number];

/** The grid. It is what the tab is FOR: when and where, side by side. */
export const DEFAULT_SCHEDULE_VIEW: ScheduleViewParam = "grid";

/**
 * The per-tab NARROWING of a list, in the URL for the same reason everything
 * else here is: "look at the unfinished games in Open" should be a link you
 * can paste into a chat, and a reload at a pitch should land where the
 * organiser was rather than on an unfiltered list of sixty rows.
 *
 * One `filter` param, three vocabularies, exactly like `view` is shared by the
 * Spirit and Stats tabs: they are never open at once, each tab validates the
 * value against its OWN list, and an unrecognised one means "no filter"
 * instead of an empty table.
 */
export const SCHEDULE_FILTERS = ["finished", "underway", "upcoming"] as const;
export type ScheduleFilterParam = (typeof SCHEDULE_FILTERS)[number];

/** Review has exactly one filter worth having: what still needs work. */
export const REVIEW_FILTERS = ["attention"] as const;
export type ReviewFilterParam = (typeof REVIEW_FILTERS)[number];

/**
 * Rows per page, and the sizes offered.
 *
 * TEN by default. The list this pages is every link ever issued, and what an
 * organiser comes here to do is find one person's row, not read the archive:
 * a short page that fits a phone screen beats a long one they scroll past.
 */
export const PAGE_SIZES = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 10;

/** A grant's lifecycle. `unopened` is the one that matters on event morning:
 *  it says who never received their link. */
export const LINK_FILTERS = ["active", "unopened", "revoked", "expired"] as const;
export type LinkFilterParam = (typeof LINK_FILTERS)[number];

const ALL_FILTERS: readonly string[] = [...SCHEDULE_FILTERS, ...REVIEW_FILTERS, ...LINK_FILTERS];

export function asScheduleFilter(v: string | undefined): ScheduleFilterParam | undefined {
  return v !== undefined && (SCHEDULE_FILTERS as readonly string[]).includes(v)
    ? (v as ScheduleFilterParam)
    : undefined;
}

export function asReviewFilter(v: string | undefined): ReviewFilterParam | undefined {
  return v !== undefined && (REVIEW_FILTERS as readonly string[]).includes(v)
    ? (v as ReviewFilterParam)
    : undefined;
}

export function asLinkFilter(v: string | undefined): LinkFilterParam | undefined {
  return v !== undefined && (LINK_FILTERS as readonly string[]).includes(v)
    ? (v as LinkFilterParam)
    : undefined;
}

export interface AdminSearch {
  /** Division the bracket editor (or the spirit standings) is showing. */
  division?: string;
  /** Finished game open in the review editor, or in the spirit detail. */
  game?: string;
  /** Which spirit, stats or schedule sub-view is open. */
  view?: SpiritViewParam | StatsViewParam | ScheduleViewParam;
  /** Player whose detail card is open in the Stats tab. */
  player?: string;
  /** Free-text search over whatever the open tab's list is made of. */
  q?: string;
  /** Team the Schedule tab is narrowed to. */
  team?: string;
  /** Pool the Teams tab is narrowed to (its division filter shares `division`
   *  with the bracket editor - same reasoning as the shared `view`/`filter`). */
  pool?: string;
  /** Calendar day the Schedule tab is narrowed to, as the stored zone-less
   *  local date ("2026-09-05"). */
  day?: string;
  /** The open tab's own narrowing. See the three lists above. */
  filter?: string;
  /** 1-based page, for the one list that grows without limit. */
  page?: number;
  /** Rows per page on that list. One of `PAGE_SIZES`. */
  per?: number;
}

/**
 * A patch of the view state, where `undefined` means REMOVE the key.
 *
 * `Partial<AdminSearch>` cannot express that under
 * `exactOptionalPropertyTypes`: there an optional key may be MISSING but may
 * not hold `undefined`, and "drop this filter" has to be sayable.
 */
export type AdminSearchPatch = { [K in keyof AdminSearch]?: AdminSearch[K] | undefined };

/**
 * WHAT THIS PARSER CANNOT DO: keep a param out of the component.
 *
 * Dropping `?team=` here removes it from what this function returns, and the
 * router still passes the raw query through, so a panel reading `search.team`
 * can be handed `""`. Read every optional param through `param()` in
 * `src/lib/table-filter.ts` rather than testing `!== undefined`: a filter for
 * the empty id matches nothing and hides the whole list.
 */

/** The `$tab` path segment, checked before the admin chunk is even fetched. */
export function isAdminTab(v: unknown): v is AdminTab {
  return typeof v === "string" && (ADMIN_TABS as readonly string[]).includes(v);
}

function isFilter(v: unknown): v is string {
  return typeof v === "string" && ALL_FILTERS.includes(v);
}

/** A search box's contents. Bounded: a URL is not a text field, and a
 *  thousand-character `?q=` is somebody probing rather than searching. */
function query(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const trimmed = v.trim();
  return trimmed === "" || trimmed.length > 60 ? undefined : trimmed;
}

/** A stored local date, never parsed into an instant: `2026-09-05`. */
function day(v: unknown): string | undefined {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined;
}

/**
 * Search params arrive as STRINGS, so a page number has to be read back out of
 * one — but not with `parseInt`, which answers 1 for "1e6" and 3 for "3abc".
 * A parser that silently keeps the first digit of something else is how a URL
 * nobody wrote turns into a page somebody lands on.
 *
 * Out of range is dropped rather than clamped: `pageOf` clamps against the
 * real list, which this parser cannot see.
 */
function pageNumber(v: unknown): number | undefined {
  const n =
    typeof v === "number" ? v : typeof v === "string" && /^\d+$/.test(v) ? Number(v) : Number.NaN;
  return Number.isInteger(n) && n >= 1 && n <= 9999 ? n : undefined;
}

/** Only the sizes we offer: an arbitrary `?per=5000` in a URL is a page that
 *  renders every grant ever issued. */
function perPage(v: unknown): number | undefined {
  const n = typeof v === "number" ? v : typeof v === "string" && /^\d+$/.test(v) ? Number(v) : 0;
  return (PAGE_SIZES as readonly number[]).includes(n) ? n : undefined;
}

function isView(v: unknown): v is SpiritViewParam | StatsViewParam | ScheduleViewParam {
  if (typeof v !== "string") return false;
  return (
    (SPIRIT_VIEWS as readonly string[]).includes(v) ||
    (STATS_VIEWS as readonly string[]).includes(v) ||
    (SCHEDULE_VIEWS as readonly string[]).includes(v)
  );
}

/** Ids are opaque, so only their shape is checked; a stale one just misses. */
function id(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 && v.length <= 64 ? v : undefined;
}

/** The open view AS A SPIRIT VIEW, or that tab's default. The two tabs share
 *  one param, so each narrows it to its own list on the way in. */
export function asSpiritView(v: string | undefined): SpiritViewParam {
  return v !== undefined && (SPIRIT_VIEWS as readonly string[]).includes(v)
    ? (v as SpiritViewParam)
    : DEFAULT_SPIRIT_VIEW;
}

export function asStatsView(v: string | undefined): StatsViewParam {
  return v !== undefined && (STATS_VIEWS as readonly string[]).includes(v)
    ? (v as StatsViewParam)
    : DEFAULT_STATS_VIEW;
}

export function asScheduleView(v: string | undefined): ScheduleViewParam {
  return v !== undefined && (SCHEDULE_VIEWS as readonly string[]).includes(v)
    ? (v as ScheduleViewParam)
    : DEFAULT_SCHEDULE_VIEW;
}

/**
 * Every param is optional and an unrecognised value is DROPPED, never kept: a
 * hand-edited or stale link must never produce a blank list or an empty admin.
 */
export function parseAdminSearch(search: Record<string, unknown>): AdminSearch {
  const division = id(search["division"]);
  const game = id(search["game"]);
  const player = id(search["player"]);
  const view = search["view"];
  const filter = search["filter"];
  const q = query(search["q"]);
  const team = id(search["team"]);
  const pool = id(search["pool"]);
  const when = day(search["day"]);
  const page = pageNumber(search["page"]);
  const per = perPage(search["per"]);
  return {
    ...(division !== undefined ? { division } : {}),
    ...(game !== undefined ? { game } : {}),
    ...(player !== undefined ? { player } : {}),
    ...(isView(view) ? { view } : {}),
    ...(q !== undefined ? { q } : {}),
    ...(team !== undefined ? { team } : {}),
    ...(pool !== undefined ? { pool } : {}),
    ...(when !== undefined ? { day: when } : {}),
    ...(isFilter(filter) ? { filter } : {}),
    ...(page !== undefined ? { page } : {}),
    ...(per !== undefined ? { per } : {}),
  };
}
